import { useState, Dispatch, SetStateAction } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { AnchoredPopover, OptionsPopover, OptionsPopoverOption } from "@foodpilot/foods";
import { ScoreWithVariation } from "src/services/ScoreService";
import { useEntityScores } from "src/context/Scores/useEntityScores";

export type UnitSwitcherProps = {
    score: ScoreWithVariation;
};

export const UnitSwitcher = (props: UnitSwitcherProps) => {
    const { score } = props;
    const theme = useTheme();
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);

    const { scoresWithVariations, selectedVariations } = useEntityScores();

    const selectedVariationId = selectedVariations.query?.[score.scoreId] ?? null;
    const selectedVariation =
        scoresWithVariations.find(
            (scoreWithVariation) =>
                scoreWithVariation.scoreId === score.scoreId && scoreWithVariation.variationId === selectedVariationId,
        ) ?? scoresWithVariations[0];
    const displayLabel = selectedVariation?.label;

    const handleOpenPopover = (e: React.MouseEvent<HTMLElement>) => {
        setAnchor(e.currentTarget);
    };

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                gap="4px"
                onClick={handleOpenPopover}
                sx={{
                    cursor: "pointer",
                    opacity: 1,
                }}
            >
                <Typography variant="body">{displayLabel}</Typography>
                <KeyboardArrowDown sx={{ width: "16px", height: "16px", color: theme.custom.grey[2800] }} />
            </Stack>

            {anchor && selectedVariation && (
                <UnitSwitcherPopover selectedVariation={selectedVariation} anchor={anchor} setAnchor={setAnchor} />
            )}
        </Box>
    );
};

type UnitSwitcherPopoverProps = {
    selectedVariation: ScoreWithVariation;
    anchor: null | HTMLElement;
    setAnchor: Dispatch<SetStateAction<null | HTMLElement>>;
};

const UnitSwitcherPopover = (props: UnitSwitcherPopoverProps) => {
    const { selectedVariation, anchor, setAnchor } = props;
    const isOpen = Boolean(anchor);

    const { scoresWithVariations, selectVariation } = useEntityScores();
    const displayedVariations = scoresWithVariations.filter(
        (variation) => variation.scoreId === selectedVariation.scoreId,
    );

    const handleChange = (option: OptionsPopoverOption) => {
        const variationId = Number(option.id);

        selectVariation(selectedVariation.scoreId, variationId || null);
        setAnchor(null);
    };

    return (
        <AnchoredPopover
            variant="headless"
            anchor={anchor}
            open={isOpen}
            onClose={() => setAnchor(null)}
            anchorPosition="center"
        >
            <OptionsPopover
                content={[
                    {
                        header: selectedVariation.group,
                        options: displayedVariations.map((variation: ScoreWithVariation) => {
                            return {
                                id: variation.variationId ?? 0,
                                label: variation.label,
                            };
                        }),
                    },
                ]}
                onChange={handleChange}
                defaultIndex={displayedVariations.findIndex((score) => score.rowId === selectedVariation.rowId) ?? 0}
                minWidth="287px"
            />
        </AnchoredPopover>
    );
};
